// Imports
import { AccountCircle, Add, Cancel, ContentCopyRounded, InfoOutlined, ShareRounded } from '@mui/icons-material';
import { Button, Chip, Fab, Paper, Switch, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Box } from '@mui/system';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAuthCookie } from '../auth/AuthUtil';
import { CalculationBasis, CalculationType, HubModel, VendorModel, VendorPaymentMode } from '../utils/Models';
import { Colors, DEFAULT_LOADER_STYLES, NavTabIndices, Urls } from '../../config/Constants';
import { GbkFieldType, GobikesField } from '../utils/GobikesField';
import { getVendorHubs, getVendorProfile, setVendorHubs, setVendorProfile } from '../../features/vendorProfile';
import { getHubSelection, setHubSelection } from '../../features/hubSelection';
import { GobikesApi } from '../../config/Apis';
import { DashboardLoader } from '../utils/LocalStateUtils';
import { betweenValuesValidationFn, nonNegativeValidationFn, parseFalsyFields, parseInvalidFields } from '../utils/ValidationUtils';
import { setNavSelection } from '../../features/navSelection';

// Types and constants
const profileSections = ['contact', 'location', 'operational', 'delivery', 'settings'] as const;
const passwordPlaceholder = "********";
type profileSectionsType = typeof profileSections[number];
type editModeType = { [k in profileSectionsType]: boolean };
interface LocalState {
  editMode: editModeType;
  loading?: boolean;
  error?: boolean;
  message?: string;
  resetError?: () => any
}
interface FormState {
  personal: {
    name: string,
    company: string,
    phone: string,
    otherPhone?: string
  },
  contact: {
    phone: string,
    otherPhone?: string,
    email: string,
    otherEmail?: string
  },
  settings: {
    newPassword?: string;
    noCustomerNotifications?: boolean;
  },
  location: {
    landmark: string,
    fullAddress: string,
    // howToReach: string,
    // neighbourhood: string,
    latitude: number,
    longitude: number
  },
  payment: {
    priceCalculation: CalculationBasis,
    paymentMode: VendorPaymentMode,
    split: number,
    currentBalance?: number;
    nextSettlementDate?: string;
    nextSettlementAmount?: number;
    accountHolder?: string,
    accountNumber?: string,
    bankName?: string,
    bankIfsc?: string
    accountVerified: boolean
  },
  operational: {
    delivery: boolean,
    deliveryPrice05?: number,
    deliveryPrice10?: number,
    deliveryPrice20?: number,
    deliveryStartTime?: number,
    deliveryEndTime?: number,
    opStart: number,
    opEnd: number,
    minDuration: number,
    deliveryMinDays: number,
    closingDays: number[],
    tnc: string[]
  },
}
const DEFAULT_FORM_STATE: FormState = {
  personal: { name: '', company: '', phone: '', otherPhone: '' },
  contact: { phone: '', email: '' },
  settings: { newPassword: '' },
  location: { landmark: '', fullAddress: '', /* howToReach: '', neighbourhood: '', */ latitude: 0, longitude: 0 },
  payment: { split: 0, accountVerified: false, paymentMode: VendorPaymentMode.ALL_ONLINE_AND_OFFLINE, priceCalculation: { calcType: CalculationType.HOURLY } },
  operational: { delivery: false, opStart: 0, opEnd: 24, minDuration: 0, deliveryMinDays: 0, deliveryStartTime: undefined, deliveryEndTime: undefined, tnc: [], closingDays: [] }
}
const OP_HOURS_OPTIONS = [
  ...(new Array(12).fill(0).map((_, i) => ({ value: i, display: (i === 0 ? 12 : i).toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false }) + ':00 AM' }))),
  ...(new Array(12).fill(0).map((_, i) => ({ value: i + 12, display: (i === 0 ? 12 : i).toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false }) + ':00 PM' })))
]
const NUMBER_TO_DAY_MAPPING: { [k: number]: string } = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  7: 'Sun'
};
const DAY_TO_NUMBER_MAPPING: { [k: string]: number } = {
  'Mon': 1,
  'Tue': 2,
  'Wed': 3,
  'Thu': 4,
  'Fri': 5,
  'Sat': 6,
  'Sun': 7
}

// Main app component
export function ProfilePage(props: {
  window?: () => Window;
  drawerWidth: number
}) {

  // Local state
  const [state, setState] = useState<LocalState>({ editMode: profileSections.reduce((a, v) => ({ ...a, [v]: false }), {}) as editModeType });
  const [formState, setFormState] = useState<FormState>(DEFAULT_FORM_STATE);
  const [linkCopied, setLinkCopied] = useState(false);

  // Global state and constants
  const profile = useSelector(getVendorProfile);
  const hubs = useSelector(getVendorHubs);
  const selectedHub = useSelector(getHubSelection);
  const dispatch = useDispatch();
  const authCookie = getAuthCookie();
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up('md'));

  // Set nav state
  useEffect(() => {
    dispatch(setNavSelection(NavTabIndices.PROFILE));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Create state from profile and selected hub
  useEffect(() => {
    setFormState({
      personal: {
        name: profile.name,
        company: profile.company,
        phone: profile.phone,
        otherPhone: profile.otherPhone
      },
      contact: {
        phone: selectedHub.phone,
        otherPhone: selectedHub.otherPhone,
        email: selectedHub.email,
        otherEmail: selectedHub.otherEmail
      },
      settings: {
        newPassword: '',
        noCustomerNotifications: profile.noCustomerNotifications || false,
      },
      location: {
        landmark: selectedHub.landmark,
        fullAddress: selectedHub.fullAddress,
        // howToReach: selectedHub.howToReach,
        // neighbourhood: selectedHub.neighbourhood,
        latitude: selectedHub.latitude,
        longitude: selectedHub.longitude
      },
      payment: {
        currentBalance: profile.cashfreeData?.balance,
        nextSettlementDate: profile.cashfreeData?.nextSettlementDetails.date,
        nextSettlementAmount: profile.cashfreeData?.nextSettlementDetails.settlementAmount,
        paymentMode: profile.paymentMode,
        priceCalculation: profile.priceCalculation,
        split: profile.split,
        accountHolder: profile.accountHolder,
        accountNumber: profile.accountNumber,
        bankName: profile.bankName,
        bankIfsc: profile.bankIfsc,
        accountVerified: profile.accountVerified
      },
      operational: {
        delivery: selectedHub.delivery,
        deliveryPrice05: selectedHub.deliveryCharges?.upto05 || 0,
        deliveryPrice10: selectedHub.deliveryCharges?.upto10 || 0,
        deliveryPrice20: selectedHub.deliveryCharges?.upto20 || 0,
        deliveryStartTime: selectedHub.deliveryStartTime ?? undefined,
        deliveryEndTime: selectedHub.deliveryEndTime ?? undefined,
        opStart: selectedHub.opStart,
        opEnd: selectedHub.opEnd,
        minDuration: selectedHub.minDuration,
        deliveryMinDays: selectedHub.deliveryMinDays,
        closingDays: [...(selectedHub.closingDays ? selectedHub.closingDays : [])],
        tnc: [...(selectedHub.tnc ? selectedHub.tnc : [])]
      }
    })
  }, [profile, selectedHub]);

  // Update active status
  const updateActiveStatus = (activeStatus: boolean) => {
    const payload = { activeStatus };
    setState({ loading: true, editMode: state.editMode });
    (async () => {
      try {
        const { data } = await GobikesApi.post(`/vendors/profile?returnProfile=true`, payload, { headers: { 'vendor-token': authCookie } });
        const vendor: VendorModel = data.data;
        dispatch(setVendorProfile(vendor));
        setState({ loading: false, ...state });
      } catch (e: any) {
        const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
        setState({ error: true, message: errorMessage, loading: false, ...state })
      }
    })();
  }

  // Save functions
  function saveHubContactDetails() {
    const payload = formState.contact;
    const invalidFields = parseFalsyFields([
      { name: 'Primary phone', value: payload.phone },
      { name: 'Primary email', value: payload.email },
    ]);
    if (invalidFields.length) {
      setState({
        error: true,
        message: `Please enter these required fields: ${invalidFields.join(', ')}`,
        editMode: state.editMode,
        resetError: () => { setState({ error: false, editMode: state.editMode }); }
      });
      return;
    }
    setState({ loading: true, editMode: state.editMode });
    (async () => {
      try {
        const { data } = await GobikesApi.put(`/vendors/hubs/${selectedHub.id}?returnAllHubs=true`, payload, { headers: { 'vendor-token': authCookie } });
        const vendorHubs: HubModel[] = data.data;
        setState({ loading: false, editMode: { ...state.editMode, contact: false } });
        dispatch(setVendorHubs(vendorHubs));
        dispatch(setHubSelection(vendorHubs.find(a => a.id === selectedHub.id)));
      } catch (e: any) {
        const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
        setState({ error: true, message: errorMessage, loading: false, editMode: state.editMode, resetError: () => { setState({ error: false, editMode: state.editMode }); } })
      }
    })();
  }
  // function saveHubLocationDetails() {
  //   const payload = formState.location;
  //   const invalidFields = parseFalsyFields([
  //     { name: 'Landmark', value: payload.landmark },
  //     { name: 'Full Address', value: payload.fullAddress },
  //   ]);
  //   if (invalidFields.length) {
  //     setState({
  //       error: true,
  //       message: `Please enter these required fields: ${invalidFields.join(', ')}`,
  //       editMode: state.editMode,
  //       resetError: () => { setState({ error: false, editMode: state.editMode }); }
  //     });
  //     return;
  //   }
  //   setState({ loading: true, editMode: state.editMode });
  //   (async () => {
  //     try {
  //       const { data } = await GobikesApi.put(`/vendors/hubs/${selectedHub.id}?returnAllHubs=true`, payload, { headers: { 'vendor-token': authCookie } });
  //       const vendorHubs: HubModel[] = data.data;
  //       setState({ loading: false, editMode: { ...state.editMode, location: false } });
  //       dispatch(setVendorHubs(vendorHubs));
  //       dispatch(setHubSelection(vendorHubs.find(a => a.id === selectedHub.id)));
  //     } catch (e: any) {
  //       const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
  //       setState({ error: true, message: errorMessage, loading: false, editMode: state.editMode, resetError: () => { setState({ error: false, editMode: state.editMode }); } })
  //     }
  //   })();
  // }
  function saveHubOperationalDetails() {
    const payload: any = { ...formState.operational };
    payload.deliveryCharges = { upto05: payload.deliveryPrice05, upto10: payload.deliveryPrice10, upto20: payload.deliveryPrice20 };
    const invalidFields = parseInvalidFields([
      { name: 'Operational Hours Start', value: payload.opStart, validateFn: nonNegativeValidationFn() },
      { name: 'Operational Hours End', value: payload.opEnd, validateFn: nonNegativeValidationFn() }
    ]);
    if (payload.delivery && !payload.deliveryPrice05 && !payload.deliveryPrice10 && !payload.deliveryPrice20) {
      invalidFields.push('at least one delivery price field');
    }
    if (invalidFields.length) {
      setState({
        error: true,
        message: `Please enter these required fields: ${invalidFields.join(', ')}`,
        editMode: state.editMode,
        resetError: () => { setState({ error: false, editMode: state.editMode }); }
      });
      return;
    }
    setState({ loading: true, editMode: state.editMode });
    (async () => {
      try {
        const { data } = await GobikesApi.put(`/vendors/hubs/${selectedHub.id}?returnAllHubs=true`, payload, { headers: { 'vendor-token': authCookie } });
        const vendorHubs: HubModel[] = data.data;
        setState({ loading: false, editMode: { ...state.editMode, operational: false } });
        dispatch(setVendorHubs(vendorHubs));
        dispatch(setHubSelection(vendorHubs.find(a => a.id === selectedHub.id)));
      } catch (e: any) {
        const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
        setState({ error: true, message: errorMessage, loading: false, editMode: state.editMode, resetError: () => { setState({ error: false, editMode: state.editMode }); } })
      }
    })();
  }
  function saveHubDeliveryDetails() {
    const payload: any = {
      delivery: formState.operational.delivery,
      deliveryMinDays: formState.operational.deliveryMinDays,
      deliveryCharges: { upto05: formState.operational.deliveryPrice05, upto10: formState.operational.deliveryPrice10, upto20: formState.operational.deliveryPrice20 },
    };
    payload.deliveryStartTime = formState.operational.deliveryStartTime ?? null;
    payload.deliveryEndTime = formState.operational.deliveryEndTime ?? null;
    if (payload.delivery && !payload.deliveryCharges.upto05 && !payload.deliveryCharges.upto10 && !payload.deliveryCharges.upto20) {
      setState({ error: true, message: 'Please enter at least one delivery price field', editMode: state.editMode, resetError: () => { setState({ error: false, editMode: state.editMode }); } });
      return;
    }
    if (payload.delivery && (payload.deliveryStartTime == null || payload.deliveryEndTime == null)) {
      setState({ error: true, message: 'Please select Delivery Start Time and Delivery End Time', editMode: state.editMode, resetError: () => { setState({ error: false, editMode: state.editMode }); } });
      return;
    }
    setState({ loading: true, editMode: state.editMode });
    (async () => {
      try {
        const { data } = await GobikesApi.put(`/vendors/hubs/${selectedHub.id}?returnAllHubs=true`, payload, { headers: { 'vendor-token': authCookie } });
        const vendorHubs: HubModel[] = data.data;
        setState({ loading: false, editMode: { ...state.editMode, delivery: false } });
        dispatch(setVendorHubs(vendorHubs));
        dispatch(setHubSelection(vendorHubs.find(a => a.id === selectedHub.id)));
      } catch (e: any) {
        const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
        setState({ error: true, message: errorMessage, loading: false, editMode: state.editMode, resetError: () => { setState({ error: false, editMode: state.editMode }); } })
      }
    })();
  }

  const saveAccountSettings = () => {
    const payload: { password?: string, noCustomerNotifications?: boolean } = { noCustomerNotifications: formState.settings.noCustomerNotifications || false };
    if (formState.settings.newPassword) payload.password = formState.settings.newPassword;
    const passwordError = payload.password && payload.password.length < 8 ? 'Password should contain minimum 8 characters.' : null;
    if (passwordError) {
      setState({
        error: true,
        message: passwordError,
        editMode: state.editMode,
        resetError: () => { setState({ error: false, editMode: state.editMode }); }
      });
      return;
    }
    setState({ loading: true, editMode: state.editMode });
    (async () => {
      try {
        const { data } = await GobikesApi.post(`/vendors/profile?returnProfile=true`, payload, { headers: { 'vendor-token': authCookie } });
        const vendor: VendorModel = data.data;
        dispatch(setVendorProfile(vendor));
        setState({ loading: false, ...state, editMode: { ...state.editMode, settings: false } });
      } catch (e: any) {
        const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
        setState({ error: true, message: errorMessage, loading: false, ...state })
      }
    })();
  }

  const getHubShareLink = () => {
    const citySlug = (profile.city?.name || '').toLowerCase().split(' ').join('-');
    return `${Urls.MAIN_SITE}/in/${citySlug}/bike-rentals?hub=${selectedHub.id}`;
  }

  const copyHubShareLink = () => {
    navigator.clipboard.writeText(getHubShareLink());
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  }

  const shareHubLink = async () => {
    const link = getHubShareLink();
    if (navigator.share) {
      try {
        await navigator.share({ title: `${selectedHub.landmark} on GoWheelo`, url: link });
      } catch (e) {
        // User cancelled the share sheet or it failed silently; nothing to do
      }
    } else {
      copyHubShareLink();
    }
  }

  // Loading view
  let mainContent = <div></div>;
  if (state.loading) {
    mainContent = <DashboardLoader
      {...state}
      styles={DEFAULT_LOADER_STYLES}
    />
  }

  // Error view
  else if (state.error) {
    mainContent = <DashboardLoader
      {...state}
      styles={DEFAULT_LOADER_STYLES}
    />
  }

  // Individual block view
  else {
    mainContent = (
      <Fragment>
        <div className="gbk-paper-container">

          {/* Empty space at top for phone screen */}
          <Fragment>
            <Box sx={{ display: { xs: 'block', md: 'none', height: '18px' }, width: 1 }}>
              <Box className="phone-fab" style={{ height: 'auto' }}>
                <br />
              </Box> <br />
            </Box>
          </Fragment>

          {renderPersonalDetails(false)}
          {renderVendorPaymentDetails(false)}
          {renderVendorAccountSettings()}
          {/* {renderVendorContactDetails(state.editMode.contact)} */}
          {selectedHub?.id && renderHubContactDetails(state.editMode.contact)}
          {selectedHub?.id && renderHubLocationDetails(state.editMode.location)}
          {selectedHub?.id && renderHubOperationalDetails()}
          {selectedHub?.id && renderHubDeliveryDetails()}
        </div>
      </Fragment >
    )
  }

  // Return DOM
  return (
    <Box sx={{ mt: { xs: '50px', md: 0 }, ml: { xs: 0, md: `${props.drawerWidth}px` }, width: { xs: '100%', md: `calc(100% - ${props.drawerWidth}px)` }, height: '100%' }}>
      <div className='gbk-tabcontent allow-scroll'>

        {selectedHub?.id && (
          <Button
            onClick={copyHubShareLink}
            className='gbk-fab gbk-fab-top-right'
            sx={{ display: { xs: 'none', md: 'inline-flex' }, right: '240px !important' }}
            variant='contained'
            size='large'
            color='primary'
            disabled={state.loading}
          >
            <ContentCopyRounded fontSize='small' /> &nbsp; {linkCopied ? 'Link Copied!' : 'Share Unique Link'}
          </Button>
        )}

        {/* Share icon fab (mobile) */}
        {selectedHub?.id && (
          <Fab
            size='small'
            color='primary'
            disabled={state.loading}
            onClick={shareHubLink}
            className='gbk-fab gbk-fab-top-right'
            sx={{ display: { xs: 'inline-flex', md: 'none' }, right: '145px !important', boxShadow: 2 }}
          >
            <ShareRounded fontSize='small' />
          </Fab>
        )}

        {/* Edit profile active fab */}
        <Fab
          className={`gbk-fab gbk-fab-top-right gbk-fab-no-style ${profile.adminBlocked ? 'large-text' : ''}`}
          sx={{ display: { xs: 'block', md: 'inline-flex' }, background: '#eee !important' }}
          variant='extended'
          color='primary'
          disabled={state.loading}
          size={isPhone ? 'small' : 'large'}
        >
          {renderActiveFab()}
        </Fab>

        {/* Header */}
        <Box className='tab-header' sx={{ display: { xs: 'none', md: 'block' } }}>

          {/* Icon */}
          <div className={`tab-header-fab`}  > <AccountCircle fontSize="large" /></div>

          {/* Text */}
          <Typography>
            <span style={{ ...(state.editMode && { color: Colors.primary }) }}>
              Profile
            </span>
            {` / ${selectedHub.landmark}`}
          </Typography>
          {profile.certifiedDealer && (
            <Chip
              label="Certified Dealer"
              size="small"
              sx={{ ml: 1.5, backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, border: '1px solid #a5d6a7' }}
            />
          )}
        </Box>

        {/* Main content */}
        {mainContent}

      </div >
    </Box >
  )

  // Render personal details
  function renderPersonalDetails(editMode: boolean) {
    return <Fragment>
      <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
        <div className="header">
          <Typography className="header-left" fontSize={18} fontWeight={500}>Partner Details</Typography>
          <Typography className={`sub ${isPhone ? 'header-left' : 'header-right'}`}>Contact GoWheelo for modification</Typography>
        </div>
        <div className="content" >
          <div className="gbk-col" style={{ minWidth: 400 }}>
            <GobikesField
              type={GbkFieldType.STRING}
              label={'Partner Name'}
              editMode={false}
              value={formState.personal.name || ''}
            />
          </div>
          <div className="gbk-col" style={{ minWidth: 400 }}>
            <GobikesField
              type={GbkFieldType.STRING}
              label={'Company Name'}
              editMode={false}
              value={formState.personal.company || ''}
            />
          </div>
          <br />
          <div className="gbk-col" style={{ minWidth: 400 }}>
            <GobikesField
              type={GbkFieldType.STRING}
              label={'Primary Phone'}
              editMode={false}
              value={formState.personal.phone || ''}
            />
          </div>
          <div className="gbk-col" style={{ minWidth: 400 }}>
            <GobikesField
              type={GbkFieldType.STRING}
              label={'Secondary Phone'}
              editMode={false}
              value={formState.personal.otherPhone || ''}
            />
          </div>
        </div>
      </Paper>
      <br />
    </Fragment>
  }

  // Payment details
  function renderVendorPaymentDetails(editMode: boolean) {
    return <Fragment>
      <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
        <div className="header">
          <Typography className="header-left" fontSize={18} fontWeight={500}>Partner Payment Details</Typography>
          <Typography className={`sub ${isPhone ? 'header-left' : 'header-right'}`}>Contact GoWheelo for modification</Typography>
        </div>
        <div className="content" >
          <div className="gbk-col" style={{ minWidth: 400 }}>
            {/* Current balance */}
            <GobikesField
              type={GbkFieldType.STRING}
              label={'Current Balance'}
              editMode={false}
              value={formState.payment.currentBalance !== undefined ? `₹ ${formState.payment.currentBalance}` : 'Failed to load'}
            />
            <br /><br />
            {/* Payment mode */}
            <GobikesField
              type={GbkFieldType.STRING}
              label={'Payment Mode'}
              editMode={false}
              value={{
                [VendorPaymentMode.PREPAID_ONLY]: 'Prepaid only',
                [VendorPaymentMode.POSTPAID_ONLY]: 'Postpaid only',
                [VendorPaymentMode.OFFLINE_ONLY]: 'Offline only',
                [VendorPaymentMode.PREPAID_AND_POSTPAID]: 'Prepaid and Postpaid allowed',
                [VendorPaymentMode.PREPAID_AND_OFFLINE]: 'Prepaid and Offline allowed',
                [VendorPaymentMode.POSTPAID_AND_OFFLINE]: 'Postpaid and Offline allowed',
                [VendorPaymentMode.ALL_ONLINE_AND_OFFLINE]: 'All payment modes allowed',
              }[formState.payment.paymentMode]}
            />
            <br /><br />
            {/* Calculation */}
            <GobikesField
              type={GbkFieldType.STRING}
              label={'Payment Calculation'}
              editMode={false}
              value={
                (() => {
                  if (formState.payment.priceCalculation.calcType === CalculationType.HOURLY)
                    return '24 Hours are counted as one day';
                  const startTime = formState.payment.priceCalculation.dayStart as number;
                  const endTime = formState.payment.priceCalculation.dayEnd as number;
                  const startString = `${startTime <= 12 ? startTime : startTime - 12}:00 ${startTime < 12 || startTime === 24 ? 'AM' : 'PM'}`;
                  const endString = `${endTime <= 12 ? endTime : endTime - 12}:00 ${endTime < 12 || endTime === 24 ? 'AM' : 'PM'}`;
                  return `${startString} to ${endString} counts as one day`
                })()
              }
            />
          </div>
          {profile.cashfreeData?.bank?.accountNumber ? (
            <div className="gbk-col" style={{ minWidth: 400 }}>
              <GobikesField
                type={GbkFieldType.STRING}
                label={'Bank Account Number'}
                editMode={false}
                value={profile.cashfreeData?.bank?.accountNumber}
              />
              <br /><br />
              <GobikesField
                type={GbkFieldType.STRING}
                label={'Bank IFSC'}
                editMode={false}
                value={profile.cashfreeData?.bank?.ifsc}
              />
            </div>
          ) : ''}
        </div>
      </Paper>
      <br />
    </Fragment>
  }

  // Payment details
  function renderVendorAccountSettings() {
    return <Fragment>
      <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
        <div className="header">
          <Typography className="header-left" fontSize={18} fontWeight={500}>
            Partner Account Settings
          </Typography>
          <Typography className="header-right" fontSize={16}>
            {
              profile.profileLocked ? 'Contact GoWheelo for modification' :
                state.editMode.settings
                  ? (
                    <Fragment>
                      <Button size='small' className="thick-border" variant="contained" onClick={e => saveAccountSettings()}>Save</Button>
                      <Button
                        size='small'
                        className="thick-border"
                        variant="outlined"
                        sx={{ marginLeft: 2 }}
                        onClick={e => {
                          setFormState({ ...formState, settings: { newPassword: '', noCustomerNotifications: profile.noCustomerNotifications || false } })
                          setState({ ...state, editMode: { ...state.editMode, settings: false } })
                        }}
                      >
                        Cancel
                      </Button>
                    </Fragment>
                  )
                  : (
                    <Fragment>
                      <Button size='small' className="thick-border" variant="contained" onClick={e => setState({ ...state, editMode: { ...state.editMode, settings: true } })}>Edit</Button>
                    </Fragment>
                  )
            }
          </Typography>
        </div>
        <div className="content" >
          <Box className="gbk-col" sx={{ width: { xs: '100vw', md: 600 } }}>
            <GobikesField
              type={GbkFieldType.PASSWORD}
              label={'Password'}
              editMode={state.editMode.settings}
              value={state.editMode.settings ? (formState.settings.newPassword || '') : passwordPlaceholder}
              updateFn={(v) => { setFormState({ ...formState, settings: { ...formState.settings, newPassword: v } }) }}
            />
            <br />
            <GobikesField
              type={GbkFieldType.SWITCH}
              label={'No Customer messages for offline created orders'}
              editMode={state.editMode.settings}
              value={formState.settings.noCustomerNotifications || false}
              updateFn={(v) => { setFormState({ ...formState, settings: { ...formState.settings, noCustomerNotifications: v } }) }}
            />
          </Box>
          <br />
        </div>
      </Paper>
      <br />
    </Fragment>
  }

  // // Render vendor contact details
  // function renderVendorContactDetails(editMode: boolean) {
  //   return <Fragment>
  //     <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
  //       <div className="header">
  //         <Typography className="header-left" fontSize={18} fontWeight={500}>
  //           Vendor Contact Details
  //         </Typography>
  //         <Typography className="header-right" fontSize={16}>Contact GoBikes for modification</Typography>
  //       </div>
  //       <div className="content" >
  //         <div className="gbk-col" style={{ minWidth: 400 }}>
  //           <GobikesField
  //             type={GbkFieldType.STRING}
  //             label={'Primary phone'}
  //             editMode={false}
  //             value={formState?.contact.phone || ''}
  //           />
  //         </div>
  //         <div className="gbk-col" style={{ minWidth: 400 }}>
  //           <GobikesField
  //             type={GbkFieldType.STRING}
  //             label={'Secondary phone(s)'}
  //             editMode={false}
  //             value={formState?.contact.otherPhone || ''}
  //           />
  //         </div>
  //         <br />
  //         <div className="gbk-col" style={{ minWidth: 400 }}>
  //           <GobikesField
  //             type={GbkFieldType.STRING}
  //             label={'Primary email'}
  //             editMode={false}
  //             value={formState?.contact.email || ''}
  //           />
  //         </div>
  //         <div className="gbk-col" style={{ minWidth: 400 }}>
  //           <GobikesField
  //             type={GbkFieldType.STRING}
  //             label={'Secondary email(s)'}
  //             editMode={false}
  //             value={formState?.contact.otherEmail || ''}
  //           />
  //         </div>
  //       </div>
  //     </Paper>
  //     <br /><br />
  //   </Fragment>
  // }

  // Render hub contact details
  function renderHubContactDetails(editMode: boolean) {
    return <Fragment>
      <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
        <div className="header">
          <Typography className="header-left" fontSize={18} fontWeight={500}>
            Hub Contact Details {hubs.length > 1 && <>[ <span style={{ color: Colors.primary }}>{selectedHub.landmark}</span> ]</>}
          </Typography>
          <Typography className="header-right" fontSize={16}>
            {
              profile.profileLocked ? 'Contact GoWheelo for modification' :
                state.editMode.contact
                  ? (
                    <Fragment>
                      <Button size='small' className="thick-border" variant="contained" onClick={e => saveHubContactDetails()}>Save</Button>
                      <Button
                        size='small'
                        className="thick-border"
                        variant="outlined"
                        sx={{ marginLeft: 2 }}
                        onClick={e => {
                          setFormState({ ...formState, contact: { phone: selectedHub.phone, otherPhone: selectedHub.otherPhone, email: selectedHub.email, otherEmail: selectedHub.otherEmail } })
                          setState({ ...state, editMode: { ...state.editMode, contact: false } })
                        }}
                      >
                        Cancel
                      </Button>
                    </Fragment>
                  )
                  : (
                    <Fragment>
                      <Button size='small' className="thick-border" variant="contained" onClick={e => setState({ ...state, editMode: { ...state.editMode, contact: true } })}>Edit</Button>
                    </Fragment>
                  )
            }
          </Typography>
        </div>
        <div className="content" >
          <div className="gbk-col" style={{ minWidth: 400 }}>
            <GobikesField
              type={GbkFieldType.NUMBER}
              label={'Primary Phone'}
              editMode={state.editMode.contact}
              value={formState.contact.phone || ''}
              updateFn={(v) => { setFormState({ ...formState, contact: { ...formState.contact, phone: v } }) }}
            />
          </div>
          <div className="gbk-col" style={{ minWidth: 400 }}>
            <GobikesField
              type={GbkFieldType.NUMBER}
              label={'Secondary Phone'}
              editMode={state.editMode.contact}
              value={formState.contact.otherPhone || ''}
              updateFn={(v) => { setFormState({ ...formState, contact: { ...formState.contact, otherPhone: v } }) }}
            />
          </div>
          <br />
          <div className="gbk-col" style={{ minWidth: 400 }}>
            <GobikesField
              type={GbkFieldType.STRING}
              label={'Primary Email ID'}
              editMode={state.editMode.contact}
              value={formState.contact.email || ''}
              updateFn={(v) => { setFormState({ ...formState, contact: { ...formState.contact, email: v } }) }}
            />
          </div>
          <div className="gbk-col" style={{ minWidth: 400 }}>
            <GobikesField
              type={GbkFieldType.STRING}
              label={'Secondary Email IDs'}
              editMode={state.editMode.contact}
              value={formState.contact.otherEmail || ''}
              updateFn={(v) => { setFormState({ ...formState, contact: { ...formState.contact, otherEmail: v } }) }}
            />
          </div>
        </div>
      </Paper>
      <br />
    </Fragment>
  }

  // Render hub location details
  function renderHubLocationDetails(editMode: boolean) {
    return <Fragment>
      <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
        <div className="header">
          <Typography className="header-left" fontSize={18} fontWeight={500}>
            Hub Location Details {hubs.length > 1 && <>[ <span style={{ color: Colors.primary }}>{selectedHub.landmark}</span> ]</>}
          </Typography>
          <Typography className="header-right" fontSize={16}>
            Contact GoWheelo for modification
            {
              // profile.profileLocked ? 'Contact GoBikes for modification' :
              //   state.editMode.location
              //     ? (
              //       <Fragment>
              //         <Button size='small' className="thick-border" variant="contained" onClick={e => saveHubLocationDetails()}>Save</Button>
              //         <Button
              //           size='small'
              //           className="thick-border"
              //           variant="outlined"
              //           sx={{ marginLeft: 2 }}
              //           onClick={e => {
              //             setFormState({ ...formState, location: { landmark: selectedHub.landmark, fullAddress: selectedHub.fullAddress, /* howToReach: selectedHub.howToReach, neighbourhood: selectedHub.neighbourhood, */ latitude: selectedHub.latitude, longitude: selectedHub.longitude } })
              //             setState({ ...state, editMode: { ...state.editMode, location: false } })
              //           }}
              //         >
              //           Cancel
              //         </Button>
              //       </Fragment>
              //     )
              //     : (
              //       <Fragment>
              //         <Button size='small' className="thick-border" variant="contained" onClick={e => setState({ ...state, editMode: { ...state.editMode, location: true } })}>Edit</Button>
              //       </Fragment>
              //     )
            }
          </Typography>
        </div>
        <div className="content" >
          <Box className="gbk-col" sx={{ width: { xs: '100vw', md: 600 } }}>
            <GobikesField
              type={GbkFieldType.STRING}
              label={'Landmark'}
              editMode={false}
              value={formState.location.landmark || ''}
              updateFn={(v) => { setFormState({ ...formState, location: { ...formState.location, landmark: v } }) }}
            />
          </Box>
          <br />
          <Box className="gbk-col" sx={{ width: { xs: '100vw', md: 600 } }}>
            <GobikesField
              type={GbkFieldType.STRING}
              label={'Full Address'}
              multi={3}
              editMode={state.editMode.location}
              value={formState.location.fullAddress || ''}
              updateFn={(v) => { setFormState({ ...formState, location: { ...formState.location, fullAddress: v } }) }}
            />
          </Box>
          <br />
          {/* <Box className="gbk-col" sx={{ width: { xs: '100vw', md: 600 } }}>
            <GobikesField
              type={GbkFieldType.STRING}
              label={'How to reach'}
              editMode={false}
              value={formState.location.howToReach || ''}
              updateFn={(v) => { setFormState({ ...formState, location: { ...formState.location, howToReach: v } }) }}
            />
          </Box>
          <br />
          <Box className="gbk-col" sx={{ width: { xs: '100vw', md: 600 } }}>
            <GobikesField
              type={GbkFieldType.STRING}
              label={'Neighbourhood'}
              editMode={false}
              value={formState.location.neighbourhood || ''}
              updateFn={(v) => { setFormState({ ...formState, location: { ...formState.location, neighbourhood: v } }) }}
            />
          </Box>
          <br /> */}
        </div>
      </Paper>
      <br />
    </Fragment>
  }

  // Render hub delivery details
  function renderHubDeliveryDetails() {
    return <Fragment>
      <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
        <div className="header">
          <Typography className="header-left" fontSize={18} fontWeight={500}>
            Delivery Details {hubs.length > 1 && <>[ <span style={{ color: Colors.primary }}>{selectedHub.landmark}</span> ]</>}
          </Typography>
          <Typography className="header-right" fontSize={16}>
            {
              profile.profileLocked ? 'Contact GoWheelo for modification' :
                state.editMode.delivery
                  ? (
                    <Fragment>
                      <Button size='small' className="thick-border" variant="contained" onClick={e => saveHubDeliveryDetails()}>Save</Button>
                      <Button
                        size='small'
                        className="thick-border"
                        variant="outlined"
                        sx={{ marginLeft: 2 }}
                        onClick={e => {
                          setFormState({
                            ...formState, operational: {
                              ...formState.operational,
                              delivery: selectedHub.delivery,
                              deliveryPrice05: selectedHub.deliveryCharges?.upto05 || 0,
                              deliveryPrice10: selectedHub.deliveryCharges?.upto10 || 0,
                              deliveryPrice20: selectedHub.deliveryCharges?.upto20 || 0,
                              deliveryStartTime: selectedHub.deliveryStartTime ?? undefined,
                              deliveryEndTime: selectedHub.deliveryEndTime ?? undefined,
                              deliveryMinDays: selectedHub.deliveryMinDays,
                            }
                          })
                          setState({ ...state, editMode: { ...state.editMode, delivery: false } })
                        }}
                      >
                        Cancel
                      </Button>
                    </Fragment>
                  )
                  : (
                    <Fragment>
                      <Button size='small' className="thick-border" variant="contained" onClick={e => setState({ ...state, editMode: { ...state.editMode, delivery: true } })}>Edit</Button>
                    </Fragment>
                  )
            }
          </Typography>
        </div>
        <div className="content">
          <div className="gbk-col" style={{ minWidth: 300 }}>
            <GobikesField
              type={GbkFieldType.SWITCH}
              label={'Delivery Supported'}
              editMode={state.editMode.delivery}
              value={formState.operational.delivery || false}
              updateFn={(v) => { setFormState({ ...formState, operational: { ...formState.operational, delivery: v } }) }}
            />
          </div>
          {
            formState.operational.delivery ? (
              <Fragment>
                <br />
                <div className="gbk-col" style={{ minWidth: 400 }}>
                  <GobikesField
                    type={GbkFieldType.NUMBER}
                    label={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        Min Order Duration for Delivery (Days)
                        <Tooltip title="The minimum number of days a customer must book for the delivery option to be available." arrow enterTouchDelay={0} leaveTouchDelay={4000}>
                          <InfoOutlined sx={{ fontSize: 16,  }} />
                        </Tooltip>
                      </span>
                    }
                    editMode={state.editMode.delivery}
                    value={formState.operational.deliveryMinDays}
                    updateFn={(v) => { setFormState({ ...formState, operational: { ...formState.operational, deliveryMinDays: v } }) }}
                  />
                </div>
                <br />
                <div className="gbk-col" style={{ minWidth: 300 }}>
                  <GobikesField
                    type={GbkFieldType.NUMBER}
                    label={'Price One Way (Upto 05 Km)'}
                    editMode={state.editMode.delivery}
                    value={formState.operational.deliveryPrice05}
                    updateFn={(v) => { setFormState({ ...formState, operational: { ...formState.operational, deliveryPrice05: v } }) }}
                  />
                </div>
                <div className="gbk-col" style={{ minWidth: 300 }}>
                  <GobikesField
                    type={GbkFieldType.NUMBER}
                    label={'Price One Way (Upto 10 Km)'}
                    editMode={state.editMode.delivery}
                    value={formState.operational.deliveryPrice10}
                    updateFn={(v) => { setFormState({ ...formState, operational: { ...formState.operational, deliveryPrice10: v } }) }}
                  />
                </div>
                <div className="gbk-col" style={{ minWidth: 300 }}>
                  <GobikesField
                    type={GbkFieldType.NUMBER}
                    label={'Price One Way (Upto 20 Km)'}
                    editMode={state.editMode.delivery}
                    value={formState.operational.deliveryPrice20}
                    updateFn={(v) => { setFormState({ ...formState, operational: { ...formState.operational, deliveryPrice20: v } }) }}
                  />
                </div>
                <div className="gbk-col" style={{ minWidth: 300 }}>
                  <GobikesField
                    type={GbkFieldType.SELECT}
                    label={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        Delivery Start Time
                        <Tooltip title="The earliest time your delivery service begins accepting orders each day." arrow enterTouchDelay={0} leaveTouchDelay={4000}>
                          <InfoOutlined sx={{ fontSize: 16 }} />
                        </Tooltip>
                      </span>
                    }
                    editMode={state.editMode.delivery}
                    value={formState.operational.deliveryStartTime}
                    selectOptions={OP_HOURS_OPTIONS}
                    updateFn={(v) => { setFormState({ ...formState, operational: { ...formState.operational, deliveryStartTime: v } }) }}
                  />
                </div>
                <div className="gbk-col" style={{ minWidth: 300 }}>
                  <GobikesField
                    type={GbkFieldType.SELECT}
                    label={
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        Delivery End Time
                        <Tooltip title="The latest time your delivery service stops accepting orders each day." arrow enterTouchDelay={0} leaveTouchDelay={4000}>
                          <InfoOutlined sx={{ fontSize: 16 }} />
                        </Tooltip>
                      </span>
                    }
                    editMode={state.editMode.delivery}
                    value={formState.operational.deliveryEndTime}
                    selectOptions={OP_HOURS_OPTIONS}
                    updateFn={(v) => { setFormState({ ...formState, operational: { ...formState.operational, deliveryEndTime: v } }) }}
                  />
                </div>
              </Fragment>
            ) : ''
          }
        </div>
      </Paper>
      <br />
    </Fragment>
  }

  // Render hub operational details
  function renderHubOperationalDetails() {
    return <Fragment>
      <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
        <div className="header">
          <Typography className="header-left" fontSize={18} fontWeight={500}>
            Operational Details {hubs.length > 1 && <>[ <span style={{ color: Colors.primary }}>{selectedHub.landmark}</span> ]</>}
          </Typography>
          <Typography className="header-right" fontSize={16}>
            {
              profile.profileLocked ? 'Contact GoWheelo for modification' :
                state.editMode.operational
                  ? (
                    <Fragment>
                      <Button size='small' className="thick-border" variant="contained" onClick={e => saveHubOperationalDetails()}>Save</Button>
                      <Button
                        size='small'
                        className="thick-border"
                        variant="outlined"
                        sx={{ marginLeft: 2 }}
                        onClick={e => {
                          setFormState({
                            ...formState, operational: {
                              ...formState.operational,
                              opStart: selectedHub.opStart,
                              opEnd: selectedHub.opEnd,
                              minDuration: selectedHub.minDuration,
                              tnc: selectedHub.tnc,
                              closingDays: selectedHub.closingDays || []
                            }
                          })
                          setState({ ...state, editMode: { ...state.editMode, operational: false } })
                        }}
                      >
                        Cancel
                      </Button>
                    </Fragment>
                  )
                  : (
                    <Fragment>
                      <Button size='small' className="thick-border" variant="contained" onClick={e => setState({ ...state, editMode: { ...state.editMode, operational: true } })}>Edit</Button>
                    </Fragment>
                  )
            }
          </Typography>
        </div>
        <div className="content" >
          <br />
          {/* Operational hours */}
          <div className="gbk-col" style={{ minWidth: 300 }}>
            <GobikesField
              type={GbkFieldType.SELECT}
              label={'Hub Opening Time'}
              editMode={state.editMode.operational}
              value={formState.operational.opStart}
              selectOptions={OP_HOURS_OPTIONS}
              updateFn={(v) => { setFormState({ ...formState, operational: { ...formState.operational, opStart: v } }) }}
            />
          </div>
          <div className="gbk-col" style={{ minWidth: 300 }}>
            <GobikesField
              type={GbkFieldType.SELECT}
              label={'Hub Closing Time'}
              editMode={state.editMode.operational}
              value={formState.operational.opEnd}
              selectOptions={OP_HOURS_OPTIONS}
              updateFn={(v) => { setFormState({ ...formState, operational: { ...formState.operational, opEnd: v } }) }}
            />
          </div>
          <br />
          {/* Minimum order duration */}
          <div className="gbk-col" style={{ minWidth: 300 }}>
            <GobikesField
              type={GbkFieldType.NUMBER}
              label={'Minimum Order Duration (Days)'}
              editMode={state.editMode.operational}
              value={formState.operational.minDuration}
              validateFn={nonNegativeValidationFn('Can not be less than 0')}
              updateFn={(v) => { setFormState({ ...formState, operational: { ...formState.operational, minDuration: v } }) }}
            />
          </div>
          <br />
          {/* Closing days */}
          <div className="gbk-col" style={{ minWidth: 500 }}>
            <GobikesField
              type={GbkFieldType.SELECTION_TABS}
              label={'Closing days'}
              editMode={state.editMode.operational}
              value={formState.operational.minDuration}
              validateFn={betweenValuesValidationFn(0, 24)}
              tabOptions={(() => {
                const closingDaysSet = new Set(formState.operational.closingDays);
                const options: { display: string, state: boolean }[] = [];
                for (let i = 1; i <= 7; i++) {
                  options.push({ display: NUMBER_TO_DAY_MAPPING[i], state: closingDaysSet.has(i) })
                }
                return options;
              })()}
              tabColorRed={true}
              updateFn={(v) => {
                const closingDaysSet = new Set(formState.operational.closingDays);
                const newState = [...formState.operational.closingDays];
                const day = DAY_TO_NUMBER_MAPPING[v.value];
                if (closingDaysSet.has(day)) {
                  newState.splice(newState.indexOf(day), 1)
                } else {
                  newState.push(day)
                }
                console.log(v);
                console.log(newState);
                setFormState({ ...formState, operational: { ...formState.operational, closingDays: newState } })
              }}
            />
          </div>
          <br />

          {/* Terms and conditions */}
          <div className="gbk-col no-pad-bot" style={{ minWidth: 300 }}>
            <Typography><b>Terms and Conditions</b></Typography>
          </div>
          <br />
          {!state.editMode.operational ? (  // Edit Mode OFF
            <div className="gbk-col no-pad-bot" style={{ minWidth: 300 }}>
              {!formState.operational.tnc || !formState.operational.tnc.length ? (
                <Typography variant='caption' color={'gray'}><i>No terms and conditions specified</i></Typography>
              ) : (
                <ol className='gbk-list'>
                  {formState.operational.tnc.map((a, i) => (
                    <li key={i} style={{ paddingBottom: 8 }}>{a}</li>
                  ))}
                </ol>
              )}
            </div>
          ) : ( // Edit mode ON
            <div className="gbk-col" style={{ minWidth: 600 }}>
              <br />
              {!formState.operational.tnc || !formState.operational.tnc.length ? (
                <Typography variant='caption' color={'gray'}><i>Click "Add" to enter terms and conditions</i></Typography>
              ) : (
                <Fragment>
                  {formState.operational.tnc.map((a, i) => (
                    <Fragment key={i} >
                      <Box className="gbk-col no-pad-bot" sx={{ width: { xs: '60%', md: 600 } }}>
                        <div key={`pre-${i}`} className='gbk-field-prefix'>{i + 1}.</div>
                        <GobikesField
                          key={`field-${i}`}
                          type={GbkFieldType.STRING}
                          editMode={state.editMode.operational}
                          value={a}
                          updateFn={(v) => {
                            const newTnc = formState.operational.tnc;
                            newTnc[i] = v;
                            setFormState({ ...formState, operational: { ...formState.operational, tnc: newTnc } })
                          }}
                        />
                        <span
                          className="gbk-icon-span clickable"
                          style={{ color: Colors.failureRed, verticalAlign: 'bottom', lineHeight: '54px', marginLeft: '8px' }}
                          onClick={() => {
                            const newTnc = formState.operational.tnc;
                            newTnc.splice(i, 1);
                            setFormState({ ...formState, operational: { ...formState.operational, tnc: newTnc } });
                          }}
                        >
                          <Cancel />
                        </span>
                      </Box>
                      <br />
                    </Fragment>
                  ))}
                  <br />
                  <Button
                    variant='contained'
                    onClick={() => {
                      const newTnc = [...formState.operational.tnc];
                      newTnc.push('');
                      setFormState({ ...formState, operational: { ...formState.operational, tnc: newTnc } })
                    }}
                  >
                    <Add />
                    Add new condition
                  </Button>
                </Fragment>
              )}
            </div>
          )}
        </div>
      </Paper>
      <br />
    </Fragment>
  }

  // Render FAB Content for profile active status
  function renderActiveFab() {
    return (
      <Fragment>
        <span style={{ color: state.loading ? Colors.greyDark2 : (profile.activeStatus && !profile.adminBlocked ? Colors.primary : Colors.failureRed) }}>
          {profile.activeStatus && !profile.adminBlocked ? 'ACTIVE' : 'INACTIVE'}
        </span>

        {/* Active/Inactive switch if not blocked by admin */}
        {profile.adminBlocked ? (
          <span style={{ textTransform: 'none', color: Colors.greyDark2 }}> : Blocked by Admin. Please contact GoWheelo.</span>
        ) : (
          <Switch
            checked={profile.activeStatus}
            disabled={state.loading || profile.adminBlocked || profile.profileLocked}
            onChange={(_, newState) => { updateActiveStatus(newState) }}
          />
        )}
      </Fragment>
    )
  }
}